import { Client } from '@/src/interfaces'
import { extractSubdomain } from '@/src/utils/url/domain-helpers'
import { getClientIfNotExpired } from '@/src/utils'

/**
 * Fetches client details for a given subdomain from the API.
 * @param {string} subdomain - The subdomain to fetch client details for
 * @param {Record<string, string>} [userAttributes] - Optional user attributes
 * @throws {Error} If the API call fails or the response is invalid.
 * @return {Promise<Client>} A Promise that resolves to the client details object.
 */
export const getClientIfExists = async (
  subdomain: string,
  userAttributes?: Record<string, string>
): Promise<Client> => {
  try {
    const response = await fetch(`/api/client`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ subdomain, userAttributes }),
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const data = await response.json()

    return data.client || {}
  } catch (error) {
    console.error('Error fetching client details:', error)
    throw error
  }
}

/**
 * Fetches client details for the current subdomain.
 * @throws {Error} If the API call fails or the response is invalid.
 * @return {Promise<Client>} A Promise that resolves to the client details object.
 */
export const getClientByTenant = async (): Promise<Client> => {
  const currentSubdomain = extractSubdomain(window.location.host) ?? ''

  if (!currentSubdomain) {
    return {
      id: '',
      sub_domain: '',
      insurance_carrier_network: '',
      signup_method: '1',
    } as Client
  }

  return (
    getClientIfNotExpired(currentSubdomain) ??
    (await getClientIfExists(currentSubdomain))
  )
}

/**
 * Fetches client details for the current subdomain and user attributes.
 * @throws {Error} If the API call fails or the response is invalid.
 * @param {Record<string, string>} userAttributes - The user's attributes
 * @return {Promise<Client | null>} A Promise that resolves to the client details object.
 */
export const getClientByTenantAndUserAttributes = async (
  userAttributes: Record<string, string>
): Promise<Client | null> => {
  const currentSubdomain = extractSubdomain(window.location.host) ?? ''

  if (!currentSubdomain) {
    return null
  }

  const client: Client = await getClientIfExists(
    currentSubdomain,
    userAttributes
  )

  return client?.sub_domain === currentSubdomain ? client : null
}

/**
 * Returns the default insurance value for the client from the session storage if available.
 * Else returns null.
 *
 * @returns {string | null} - The default insurance value or null if not available
 */
export const getClientDefaultInsurance = (): string | null =>
  getClientIfNotExpired('client')?.insurance_carrier_network ?? null
